/*
 * Market link rewriter.
 *
 * Ensures Ireland-market visitors stay on the /en-ie/ prefix by rewriting
 * internal same-origin <a href> attributes on load and on DOM mutation.
 * A click-time interceptor is the last line of defense in case something
 * replaces the href between our rewrite and the click.
 *
 * Server-rendered links using the `market-url` snippet already emit the
 * correct prefix; this covers app-injected markup (Rebuy, Boost, Replo,
 * Skio, third-party widgets) and any theme href we haven't wrapped.
 *
 * Runs only when window.RHEAL_IRELAND_MARKET is true (set in head-scripts).
 */
(function () {
  if (!window.RHEAL_IRELAND_MARKET) return;
  if (window.__RhealMarketLinkRewriterInit) return;
  window.__RhealMarketLinkRewriterInit = true;

  var PREFIX = '/en-ie';
  var SHOP_HOSTS = [];
  try {
    SHOP_HOSTS.push(window.location.host);
    if (window.Shopify && window.Shopify.shop) SHOP_HOSTS.push(window.Shopify.shop);
  } catch (err) {
    // ignore
  }

  // Hosts that must NEVER be prefixed, even though they live under
  // rhealsuperfoods.com — e.g. the help center, which is shared across
  // every market and already has its own fixed locale path.
  var EXCLUDED_HOSTS = ['help.rhealsuperfoods.com'];

  // Full URLs (host + path) that must never be prefixed, matched exactly
  // regardless of query string/hash. Add more as needed.
  var EXCLUDED_URLS = ['https://help.rhealsuperfoods.com/en-us'];

  // Paths that must never receive the prefix — Shopify Ajax, account,
  // localization form, and the location switcher's own targets.
  var SKIP_PATH_PREFIXES = [
    PREFIX + '/',
    '/cart/',
    '/checkout',
    '/wallets',
    '/localization',
    '/account',
    '/a/account',
    '/apps/',
    '/tools/',
    '/services/',
    '/admin',
    '/policies/'
  ];
  var SKIP_EXACT_PATHS = ['/cart', '/localization', '/account', '/a/account', PREFIX];

  function pathNeedsPrefix(pathname) {
    if (!pathname) return false;
    if (SKIP_EXACT_PATHS.indexOf(pathname) !== -1) return false;
    for (var i = 0; i < SKIP_PATH_PREFIXES.length; i += 1) {
      if (pathname.indexOf(SKIP_PATH_PREFIXES[i]) === 0) return false;
    }
    return pathname.charAt(0) === '/';
  }

  function isExcludedUrl(rawHref) {
    var url;
    try {
      url = new URL(rawHref, window.location.origin);
    } catch (err) {
      return false;
    }
    if (EXCLUDED_HOSTS.indexOf(url.host) !== -1) return true;
    // Compare host+pathname (ignore trailing slash / search / hash) against
    // the exact excluded URLs list.
    var normalized = url.origin + url.pathname.replace(/\/$/, '');
    for (var i = 0; i < EXCLUDED_URLS.length; i += 1) {
      var target = EXCLUDED_URLS[i].replace(/\/$/, '');
      if (normalized === target) return true;
    }
    return false;
  }

  function isSameOriginHref(rawHref, anchor) {
    if (!rawHref) return false;
    var lower = rawHref.trim().toLowerCase();
    if (lower.indexOf('mailto:') === 0) return false;
    if (lower.indexOf('tel:') === 0) return false;
    if (lower.indexOf('javascript:') === 0) return false;
    if (lower.charAt(0) === '#') return false;
    if (isExcludedUrl(rawHref)) return false;
    try {
      var url = new URL(rawHref, window.location.origin);
      if (EXCLUDED_HOSTS.indexOf(url.host) !== -1) return false;
      if (url.origin === window.location.origin) return true;
      // Also treat rhealsuperfoods.com absolute urls as internal.
      if (SHOP_HOSTS.indexOf(url.host) !== -1) return true;
      if (/(^|\.)rhealsuperfoods\.com$/.test(url.host)) return true;
      return false;
    } catch (err) {
      return false;
    }
  }

  function shouldSkipAnchor(anchor) {
    if (!anchor || anchor.nodeType !== 1) return true;
    if (anchor.hasAttribute('data-no-market-prefix')) return true;
    if (anchor.closest && anchor.closest('[data-location-switcher]')) return true;
    if (anchor.closest && anchor.closest('[data-no-market-prefix]')) return true;
    return false;
  }

  function buildPrefixedHref(rawHref) {
    var url;
    try {
      url = new URL(rawHref, window.location.origin);
    } catch (err) {
      return null;
    }
    if (EXCLUDED_HOSTS.indexOf(url.host) !== -1) return null;
    if (isExcludedUrl(rawHref)) return null;
    if (!pathNeedsPrefix(url.pathname)) return null;
    var newPath = url.pathname === '/' ? PREFIX : PREFIX + url.pathname;
    var isAbsolute = /^(https?:)?\/\//i.test(rawHref);
    if (isAbsolute) {
      url.pathname = newPath;
      return url.toString();
    }
    return newPath + url.search + url.hash;
  }

  function rewriteAnchor(anchor) {
    if (shouldSkipAnchor(anchor)) return;
    var href = anchor.getAttribute('href');
    if (!isSameOriginHref(href, anchor)) return;
    var prefixed = buildPrefixedHref(href);
    if (!prefixed) return;
    anchor.setAttribute('href', prefixed);
  }

  function rewriteAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var anchors = scope.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i += 1) rewriteAnchor(anchors[i]);
  }

  function onClick(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    if (shouldSkipAnchor(anchor)) return;
    var href = anchor.getAttribute('href');
    if (!isSameOriginHref(href, anchor)) return;
    var prefixed = buildPrefixedHref(href);
    if (!prefixed) return;
    // Rewrite the attribute so the browser navigates to the correct URL.
    anchor.setAttribute('href', prefixed);
  }

  function init() {
    rewriteAll(document);
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        var m = mutations[i];
        if (m.type === 'attributes' && m.target && m.target.tagName === 'A') {
          rewriteAnchor(m.target);
          continue;
        }
        for (var j = 0; j < m.addedNodes.length; j += 1) {
          var node = m.addedNodes[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'A') rewriteAnchor(node);
          if (node.querySelectorAll) rewriteAll(node);
        }
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href'],
    });
    document.addEventListener('click', onClick, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();